// stepModifierChips — the data + pure model for the call step's MODIFIER CHIPS
// popover (P3 #24). A call carries two editable trailing modifiers: an `as
// <label>` pin alias (PROCESS.md §7) and a freehand `"comment"` (§1). Each is a
// chip: click to edit (inline input → setStepModifier), × to remove (text=""),
// and an absent one is offered as a "+ add" affordance. Capability = presence,
// so this module is the single source of BOTH which chips are addable and which
// are currently present — the view just renders it.
//
// Lives in a `.ts` (not the .tsx) so the registry + model are reusable, testable
// pure data and the menu component stays a pure component export.

/** The editable trailing modifiers on a call step. */
export type StepModifierKind = "as" | "comment";

/** Static descriptor of one modifier kind — its human label, the inline-input
 *  placeholder, and how a present value renders on its chip. */
export interface StepModifierDescriptor {
  readonly kind: StepModifierKind;
  /** Short label for the "+ add" affordance + the chip's leading tag. */
  readonly label: string;
  /** Inline-edit input placeholder / aria hint. */
  readonly placeholder: string;
  /** Render a present value as the chip's body text (`as checkpoint`,
   *  `"reserve stock"`). */
  render(value: string): string;
}

/** The addable-modifier registry — the canonical order the chips + "+ add"
 *  buttons appear in (matches the wire order: comment before `as`). */
export const STEP_MODIFIER_DESCRIPTORS: readonly StepModifierDescriptor[] = [
  {
    kind: "comment",
    label: "comment",
    placeholder: "comment",
    render: (v) => `“${v}”`,
  },
  {
    kind: "as",
    label: "as",
    placeholder: "label (e.g. checkpoint)",
    render: (v) => `as ${v}`,
  },
];

/** A chip currently PRESENT on the step (a modifier that carries a value). */
export interface StepModifierChip {
  readonly kind: StepModifierKind;
  readonly descriptor: StepModifierDescriptor;
  /** The current value (label / comment text) — prefills the inline edit. */
  readonly value: string;
}

/** The current modifier values on a call step (from the WalkStep). */
export interface StepModifierValues {
  readonly asName?: string;
  readonly comment?: string;
}

/** The value carried for a kind, or undefined when absent. */
function valueOf(values: StepModifierValues, kind: StepModifierKind): string | undefined {
  return kind === "as" ? values.asName : values.comment;
}

/** The present chips, in registry order — one per modifier that carries a value. */
export function presentChips(values: StepModifierValues): readonly StepModifierChip[] {
  return STEP_MODIFIER_DESCRIPTORS.flatMap((descriptor) => {
    const value = valueOf(values, descriptor.kind);
    return value !== undefined ? [{ kind: descriptor.kind, descriptor, value }] : [];
  });
}

/** The descriptors whose modifier is ABSENT — offered as "+ add" affordances. */
export function addableDescriptors(values: StepModifierValues): readonly StepModifierDescriptor[] {
  return STEP_MODIFIER_DESCRIPTORS.filter((d) => valueOf(values, d.kind) === undefined);
}
